/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR055/CR308-A: Edit Source Modal with scan_type + web recipe override
 * CR405: Category from merchant_categories with inherit/override UX
 * CR458: Image Capture mapping section for FLYER sources
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SourceTypeSelect } from "./SourceTypeSelect";
import { WebRecipeOverrideSection } from "./WebRecipeOverrideSection";
import { type CanonicalSourceType, normalizeSourceType, getSourceTypeLabel } from "@/lib/sourceTypes";
import { AlertTriangle, Lock, Info } from "lucide-react";
import type { MerchantSource, SourceStatus, ScanType } from "@/hooks/useMerchantSources";
import { useMerchantCategoriesList, useMerchantCategoryMutations } from "@/hooks/useMerchantCategories";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
// CR458/CR459: Image Capture mapping — use shared hook for updateMapping
import { MappingEditor } from "@/components/image-capture/MappingEditor";
import { useAdapters } from "@/components/admin/image-capture-studio/useAdapters";
import { useFlyerSources } from "@/components/admin/image-capture-studio/useFlyerSources";
import type { FlyerSourceRow } from "@/lib/imageCapture/validateMapping";

/** CR405: Effective category data from view */
interface EffectiveCategoryData {
  source_id: string;
  merchant_key: string;
  category_override_id: string | null;
  merchant_category_id: string | null;
  effective_category_id: string | null;
  effective_category_name: string | null;
  effective_category_key: string | null;
}

interface EditSourceModalProps {
  source: MerchantSource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (id: string, updates: Partial<MerchantSource>) => Promise<void>;
}

export function EditSourceModal({
  source,
  open,
  onOpenChange,
  onSave,
}: EditSourceModalProps) {
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceType, setSourceType] = useState<CanonicalSourceType>("gift_card_page");
  const [scanType, setScanType] = useState<ScanType>("WEB");
  const [priority, setPriority] = useState(100);
  const [status, setStatus] = useState<SourceStatus>("active");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // CR465: Flyer Download config state
  const [downloadEnabled, setDownloadEnabled] = useState(false);
  const [downloadAdapterKey, setDownloadAdapterKey] = useState("");
  
  // CR308-A: Web recipe override state
  const [webRecipeTemplateId, setWebRecipeTemplateId] = useState<string | null>(null);

  // CR458/CR459: Image Capture adapter list + shared updateMapping mutation
  const { adapters } = useAdapters();
  const { updateMapping } = useFlyerSources();
  
  // CR405: Override state
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [selectedOverrideId, setSelectedOverrideId] = useState<string | null>(null);
  
  // CR308: Determine if scan_type is locked (official_flyer must be FLYER)
  const isScanTypeLocked = sourceType === "official_flyer";

  // CR405: Fetch categories from merchant_categories table
  const { data: categories = [], isLoading: categoriesLoading } = useMerchantCategoriesList();
  const { setSourceCategoryOverride, isSettingSourceOverride } = useMerchantCategoryMutations();

  // CR405: Fetch effective category data for this source
  const { data: effectiveCategory, refetch: refetchEffective } = useQuery({
    queryKey: ["source-effective-category", source?.id],
    queryFn: async () => {
      if (!source?.id) return null;
      const { data, error } = await supabase
        .from("v_merchant_sources_effective_category")
        .select("*")
        .eq("source_id", source.id)
        .maybeSingle();
      if (error) throw error;
      return data as EffectiveCategoryData | null;
    },
    enabled: open && !!source?.id,
  });

  // CR405: Debug logging
  useEffect(() => {
    if (open && source) {
      console.log("[EditSourceModal] CR405 render", { 
        sourceId: source.id,
        merchantId: source.merchant_id,
        effectiveCategory,
        categoriesCount: categories.length,
      });
    }
  }, [open, source, effectiveCategory, categories]);

  // Initialize form state when source changes
  useEffect(() => {
    if (source) {
      setSourceUrl(source.source_url);
      setSourceType(normalizeSourceType(source.source_type));
      setScanType(source.scan_type || "WEB");
      setPriority(source.priority);
      setStatus(source.status);
      setNotes(source.notes || "");
      setWebRecipeTemplateId(source.web_recipe_template_id ?? null);
      // CR465
      setDownloadEnabled((source as any).flyer_download_enabled ?? false);
      setDownloadAdapterKey((source as any).flyer_download_adapter_key ?? "");
      setError(null);
    }
  }, [source]);

  // CR405: Initialize override state from effective category
  useEffect(() => {
    if (effectiveCategory) {
      const hasOverride = !!effectiveCategory.category_override_id;
      setOverrideEnabled(hasOverride);
      setSelectedOverrideId(hasOverride ? effectiveCategory.category_override_id : null);
    }
  }, [effectiveCategory]);
  
  // CR308: When source_type changes to official_flyer, force scan_type to FLYER
  useEffect(() => {
    if (sourceType === "official_flyer") {
      setScanType("FLYER");
    }
  }, [sourceType]);

  // CR405: Handle override toggle
  const handleOverrideToggle = (enabled: boolean) => {
    setOverrideEnabled(enabled);
    if (!enabled) {
      setSelectedOverrideId(null);
    } else if (effectiveCategory?.merchant_category_id) {
      // Default to inherited category when enabling override
      setSelectedOverrideId(effectiveCategory.merchant_category_id);
    }
  };

  // Get inherited category name for display
  const inheritedCategoryName = categories.find(
    c => c.id === effectiveCategory?.merchant_category_id
  )?.name || "Uncategorized";

  const handleSave = async () => {
    if (!source) return;
    
    setError(null);
    
    // Validation
    if (!sourceUrl.trim()) {
      setError("Source URL is required");
      return;
    }
    
    try {
      new URL(sourceUrl);
    } catch {
      setError("Invalid URL format");
      return;
    }
    
    if (!sourceUrl.startsWith("https://")) {
      setError("URL must use HTTPS");
      return;
    }

    setSaving(true);
    try {
      // CR405: Handle category override change
      const currentOverrideId = effectiveCategory?.category_override_id ?? null;
      const newOverrideId = overrideEnabled ? selectedOverrideId : null;
      
      if (newOverrideId !== currentOverrideId) {
        await setSourceCategoryOverride({
          sourceId: source.id,
          categoryOverrideId: newOverrideId,
        });
        await refetchEffective();
      }

      // Source label is derived from source_type, not user-editable
      await onSave(source.id, {
        source_url: sourceUrl.trim(),
        source_label: getSourceTypeLabel(sourceType),
        source_type: sourceType,
        scan_type: scanType,
        priority,
        status,
        notes: notes.trim() || null,
        web_recipe_template_id: webRecipeTemplateId,
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (!source) return null;

  const isSaving = saving || isSettingSourceOverride;

  // CR458: Build a FlyerSourceRow shape for MappingEditor from the MerchantSource.
  // We don't have adapter join data here, so adapter_enabled defaults to null
  // (MappingEditor will re-derive it from the adapters list live).
  const isFlyerSource = (scanType || source.scan_type) === "FLYER";

  const flyerSourceRow: FlyerSourceRow = {
    merchant_source_id: source.id,
    merchant_id: source.merchant_id,
    merchant_name: source.merchant_name ?? null,
    scan_type: source.scan_type ?? "FLYER",
    status: source.status,
    source_url: source.source_url ?? null,
    resolved_flyer_url_template: null,
    requires_region: (source as any).requires_region ?? null,
    region_strategy: null,
    default_postal_code: (source as any).default_postal_code ?? null,
    region_cookie_payload: null,
    region_resolution_timeout_ms: null,
    flyer_image_capture_enabled: (source as any).flyer_image_capture_enabled ?? null,
    flyer_image_capture_adapter_key: (source as any).flyer_image_capture_adapter_key ?? null,
    flyer_image_capture_params: (source as any).flyer_image_capture_params ?? null,
    adapter_enabled: null,
    adapter_display_name: null,
    entrypoint: null,
    version: null,
    default_timeout_ms: null,
    param_schema: null,
    default_params: null,
    required_context: null,
    adapter_notes: null,
  };

  const studioHref = `/admin/image-capture?sourceId=${source.id}&tab=mapping`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Source</DialogTitle>
          <DialogDescription>
            Editing source for <strong>{source.merchant_name || source.merchant_id}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* CR405: Category Section with Inherit/Override UX */}
          <div className="grid gap-3 p-3 border rounded-lg bg-muted/30">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Info className="h-4 w-4 text-muted-foreground" />
                Category
              </Label>
              <Badge variant={overrideEnabled ? "default" : "secondary"}>
                {overrideEnabled ? "Override" : "Inherited"}
              </Badge>
            </div>
            
            {/* Inherited category display */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">From Merchant:</span>
              <span className="font-medium">{inheritedCategoryName}</span>
            </div>

            {/* Override toggle */}
            <div className="flex items-center justify-between pt-2 border-t">
              <Label htmlFor="override-toggle" className="text-sm">
                Override category for this source
              </Label>
              <Switch
                id="override-toggle"
                checked={overrideEnabled}
                onCheckedChange={handleOverrideToggle}
              />
            </div>

            {/* Override dropdown (shown when enabled) */}
            {overrideEnabled && (
              <div className="grid gap-2 pt-2">
                <Label className="text-sm text-muted-foreground">
                  Override Category
                </Label>
                <Select
                  value={selectedOverrideId || ""}
                  onValueChange={(val) => setSelectedOverrideId(val || null)}
                  disabled={categoriesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This source will use the selected category instead of inheriting from merchant
                </p>
              </div>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="edit_source_url">Source URL *</Label>
            <Input
              id="edit_source_url"
              type="url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Source Type</Label>
              <SourceTypeSelect value={sourceType} onChange={setSourceType} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit_priority">Priority</Label>
              <Input
                id="edit_priority"
                type="number"
                min={1}
                max={1000}
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 100)}
              />
            </div>
          </div>

          {/* CR308: Scan Type selection */}
          <div className="grid gap-2">
            <Label className="flex items-center gap-2">
              Scan Type
              {isScanTypeLocked && <Lock className="h-3 w-3 text-muted-foreground" />}
            </Label>
            <Select 
              value={scanType} 
              onValueChange={(v) => setScanType(v as ScanType)}
              disabled={isScanTypeLocked}
            >
              <SelectTrigger className={isScanTypeLocked ? "opacity-60" : ""}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="WEB">WEB - Website pipeline</SelectItem>
                <SelectItem value="FLYER">FLYER - Flyer PDF pipeline</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {isScanTypeLocked 
                ? "Official Flyer sources must use FLYER pipeline" 
                : "Determines which scan pipeline processes this source"}
            </p>
          </div>

          {/* CR308-A/CR308-C: Web Recipe Override Section */}
          {source && (
            <WebRecipeOverrideSection
              sourceId={source.id}
              scanType={scanType}
              webRecipeTemplateId={webRecipeTemplateId}
              onRecipeChange={setWebRecipeTemplateId}
              merchantName={source.merchant_name}
              disabled={isSaving}
            />
          )}

          <div className="grid gap-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as SourceStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="deprecated">Deprecated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="edit_notes">Notes (optional)</Label>
            <Textarea
              id="edit_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* CR458: Image Capture mapping — shown only for FLYER sources */}
          {isFlyerSource && (
            <div className="grid gap-3 p-3 border rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 font-semibold">
                  <span>🎯 Image Capture</span>
                </Label>
                <Badge variant="outline" className="text-xs">FLYER</Badge>
              </div>
              <MappingEditor
                source={flyerSourceRow}
                adapters={adapters}
                onSave={async (sourceId, payload) => {
                  const ok = await updateMapping(sourceId, payload);
                  if (!ok) setError("Failed to save image capture mapping");
                  return ok;
                }}
                studioHref={studioHref}
                disabled={isSaving}
              />
            </div>
          )}

          {/* CR465: Flyer Download config — shown only for FLYER sources */}
          {isFlyerSource && (
            <div className="grid gap-3 p-3 border rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 font-semibold">
                  <span>📥 Flyer Download</span>
                </Label>
                <Badge variant="outline" className="text-xs">S0.5</Badge>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="download-toggle" className="text-sm">
                  Enable Flyer Download
                </Label>
                <Switch
                  id="download-toggle"
                  checked={downloadEnabled}
                  onCheckedChange={(checked) => {
                    setDownloadEnabled(checked);
                    // Save immediately via direct update
                    if (source) {
                      supabase
                        .from("merchant_sources")
                        .update({ flyer_download_enabled: checked, updated_at: new Date().toISOString() } as any)
                        .eq("id", source.id)
                        .then(({ error: e }) => { if (e) setError("Failed to update download toggle"); });
                    }
                  }}
                  disabled={isSaving}
                />
              </div>
              {downloadEnabled && (
                <div className="grid gap-2">
                  <Label className="text-xs">Adapter Key</Label>
                  <Input
                    value={downloadAdapterKey}
                    onChange={(e) => setDownloadAdapterKey(e.target.value)}
                    placeholder="e.g. metro__flyer__download__v1"
                    className="text-xs font-mono"
                    onBlur={() => {
                      if (source) {
                        supabase
                          .from("merchant_sources")
                          .update({ flyer_download_adapter_key: downloadAdapterKey || null, updated_at: new Date().toISOString() } as any)
                          .eq("id", source.id)
                          .then(({ error: e }) => { if (e) setError("Failed to update adapter key"); });
                      }
                    }}
                    disabled={isSaving}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Python adapter entrypoint key. Leave blank to skip this source during S0.5.
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
